import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Package, Building2, CheckCircle, Factory } from 'lucide-react';

const ExternalRegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    company_name: '', vat_id_optional: '', street: '', house_number: '',
    postal_code: '', city: '', country: '', contact_name: '', contact_email: '',
    contact_phone: '', industry_optional: '', website_optional: '',
    user_name: '', user_email: '', user_password: '', confirm_password: '',
    registration_type: ''
  });

  const update = (key) => (e) => setFormData(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.user_password !== formData.confirm_password) {
      toast.error(t('ext.passwordMismatch') || 'Passwörter stimmen nicht überein');
      return;
    }
    if (formData.user_password.length < 6) {
      toast.error(t('ext.passwordTooShort') || 'Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    setLoading(true);
    try {
      const { confirm_password, ...payload } = formData;
      await axios.post(`${API}/external-companies/register`, payload);
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4" data-testid="external-register-success">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="font-manrope text-2xl">{t('ext.registrationSuccess') || 'Registrierung erfolgreich!'}</CardTitle>
            <CardDescription className="text-base mt-2">
              {t('ext.registrationPending') || 'Ihre Firma wird jetzt geprüft. Sie erhalten eine Benachrichtigung, sobald Ihr Zugang freigeschaltet wurde.'}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-3">
            <Button className="w-full bg-orange-500 hover:bg-orange-600 h-11" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
              {t('nav.login') || 'Anmelden'}
            </Button>
            <Button variant="outline" className="w-full h-11" onClick={() => navigate('/')} data-testid="go-to-home-btn">
              {t('nav.home') || 'Startseite'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4 py-8" data-testid="external-register-page">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center justify-center gap-2 mb-4">
            <Package className="h-10 w-10 text-orange-500" />
            <span className="font-manrope font-bold text-xl">Surplify</span>
          </Link>
          <CardTitle className="font-manrope text-2xl flex items-center justify-center gap-2">
            {formData.registration_type === 'group' ? (
              <Building2 className="h-6 w-6 text-orange-500" />
            ) : formData.registration_type === 'site' ? (
              <Factory className="h-6 w-6 text-orange-500" />
            ) : (
              <Building2 className="h-6 w-6 text-orange-500" />
            )}
            {t('ext.registerTitle') || 'Als externes Unternehmen registrieren'}
          </CardTitle>
          <CardDescription>
            {formData.registration_type === 'group'
              ? (t('ext.registerGroupDesc') || 'Registrieren Sie Ihre Gruppe, um mit mehreren Standorten am Marktplatz teilzunehmen.')
              : formData.registration_type === 'site'
              ? (t('ext.registerSiteDesc') || 'Registrieren Sie Ihr Werk als einzelnen Standort am Marktplatz.')
              : (t('ext.registerDesc') || 'Registrieren Sie Ihr Unternehmen, um Materialien auf dem Marktplatz anzubieten.')}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-4" data-testid="step-indicator">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${step === 0 ? 'bg-orange-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-muted-foreground'}`} onClick={() => setStep(0)}>
                0. {t('ext.registrationType') || 'Typ'}
              </div>
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${step === 1 ? 'bg-orange-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-muted-foreground'}`} onClick={() => formData.registration_type && setStep(1)}>
                1. {t('ext.companyInfo') || 'Firmendaten'}
              </div>
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${step === 2 ? 'bg-orange-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-muted-foreground'}`} onClick={() => setStep(2)}>
                2. {t('ext.userAccount') || 'Benutzerkonto'}
              </div>
            </div>

            {step === 0 && (
              <div className="space-y-4" data-testid="step-0-form">
                <p className="text-sm text-muted-foreground text-center">
                  {t('ext.chooseRegistrationType') || 'Wie möchten Sie sich registrieren?'}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    className={`flex flex-col items-center gap-3 p-6 rounded-lg border-2 transition-all ${
                      formData.registration_type === 'group'
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-orange-300'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, registration_type: 'group' }))}
                    data-testid="reg-type-group"
                  >
                    <Building2 className={`h-10 w-10 ${formData.registration_type === 'group' ? 'text-orange-500' : 'text-slate-400'}`} />
                    <span className="font-semibold text-sm">{t('ext.registerAsGroup') || 'Als Gruppe'}</span>
                    <span className="text-xs text-muted-foreground text-center">
                      {t('ext.registerAsGroupDesc') || 'Unternehmen mit mehreren Werken/Standorten'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`flex flex-col items-center gap-3 p-6 rounded-lg border-2 transition-all ${
                      formData.registration_type === 'site'
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-orange-300'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, registration_type: 'site' }))}
                    data-testid="reg-type-site"
                  >
                    <Factory className={`h-10 w-10 ${formData.registration_type === 'site' ? 'text-orange-500' : 'text-slate-400'}`} />
                    <span className="font-semibold text-sm">{t('ext.registerAsSite') || 'Als Einzelwerk'}</span>
                    <span className="text-xs text-muted-foreground text-center">
                      {t('ext.registerAsSiteDesc') || 'Einzelner Standort ohne übergeordnete Gruppe'}
                    </span>
                  </button>
                </div>
                <Button
                  type="button"
                  className="w-full bg-orange-500 hover:bg-orange-600 h-11"
                  disabled={!formData.registration_type}
                  onClick={() => setStep(1)}
                  data-testid="next-to-step-1-btn"
                >
                  {t('ext.continue') || 'Weiter'}
                </Button>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4" data-testid="step-1-form">
                <div className="space-y-2">
                  <Label>{formData.registration_type === 'group' ? (t('ext.groupName') || 'Gruppenname') : (t('ext.siteName') || 'Werkname')} *</Label>
                  <Input value={formData.company_name} onChange={update('company_name')} placeholder={formData.registration_type === 'group' ? 'Mustergruppe GmbH' : 'Musterwerk GmbH'} required data-testid="ext-company-name" />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.vatId') || 'USt-IdNr.'}</Label>
                  <Input value={formData.vat_id_optional} onChange={update('vat_id_optional')} placeholder="DE123456789" data-testid="ext-vat-id" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t('admin.contactName') || 'Ansprechpartner'} *</Label>
                    <Input value={formData.contact_name} onChange={update('contact_name')} required data-testid="ext-contact-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin.contactEmail') || 'Kontakt-E-Mail'} *</Label>
                    <Input type="email" value={formData.contact_email} onChange={update('contact_email')} required data-testid="ext-contact-email" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t('admin.contactPhone') || 'Telefon'}</Label>
                    <Input value={formData.contact_phone} onChange={update('contact_phone')} data-testid="ext-contact-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin.industry') || 'Branche'}</Label>
                    <Input value={formData.industry_optional} onChange={update('industry_optional')} data-testid="ext-industry" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t('admin.street') || 'Straße'} *</Label>
                    <Input value={formData.street} onChange={update('street')} required data-testid="ext-street" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin.houseNumber') || 'Hausnummer'} *</Label>
                    <Input value={formData.house_number} onChange={update('house_number')} required data-testid="ext-house-number" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>{t('admin.postalCode') || 'PLZ'} *</Label>
                    <Input value={formData.postal_code} onChange={update('postal_code')} required data-testid="ext-postal-code" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin.city') || 'Stadt'} *</Label>
                    <Input value={formData.city} onChange={update('city')} required data-testid="ext-city" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin.country') || 'Land'} *</Label>
                    <Input value={formData.country} onChange={update('country')} required data-testid="ext-country" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.website') || 'Website'}</Label>
                  <Input value={formData.website_optional} onChange={update('website_optional')} placeholder="https://www.example.com" data-testid="ext-website" />
                </div>
                <Button type="button" variant="outline" className="w-full h-11 mb-2" onClick={() => setStep(0)} data-testid="back-to-type-btn">
                  {t('ext.backStep') || 'Zurück'}
                </Button>
                <Button type="button" className="w-full bg-orange-500 hover:bg-orange-600 h-11" onClick={() => {
                  if (!formData.company_name || !formData.contact_name || !formData.contact_email || !formData.street || !formData.house_number || !formData.postal_code || !formData.city || !formData.country) {
                    toast.error(t('ext.fillRequired') || 'Bitte füllen Sie alle Pflichtfelder aus');
                    return;
                  }
                  setStep(2);
                }} data-testid="next-step-btn">
                  {t('ext.nextStep') || 'Weiter zum Benutzerkonto'}
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="step-2-form">
                <div className="space-y-2">
                  <Label>{t('auth.name') || 'Name'} *</Label>
                  <Input value={formData.user_name} onChange={update('user_name')} placeholder="Max Mustermann" required data-testid="ext-user-name" />
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.email') || 'E-Mail'} *</Label>
                  <Input type="email" value={formData.user_email} onChange={update('user_email')} placeholder="max@musterfirma.de" required data-testid="ext-user-email" />
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.password') || 'Passwort'} *</Label>
                  <Input type="password" value={formData.user_password} onChange={update('user_password')} placeholder="Min. 6 Zeichen" required data-testid="ext-user-password" />
                </div>
                <div className="space-y-2">
                  <Label>{t('ext.confirmPassword') || 'Passwort bestätigen'} *</Label>
                  <Input type="password" value={formData.confirm_password} onChange={update('confirm_password')} placeholder="Passwort wiederholen" required data-testid="ext-confirm-password" />
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setStep(1)} data-testid="back-step-btn">
                    {t('ext.backStep') || 'Zurück'}
                  </Button>
                  <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600 h-11" disabled={loading} data-testid="submit-registration-btn">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('ext.submitRegistration') || 'Registrierung absenden'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground text-center">
              {t('ext.alreadyRegistered') || 'Bereits registriert?'}{' '}
              <Link to="/login" className="text-orange-500 hover:underline font-medium">{t('nav.login')}</Link>
            </p>
            <p className="text-sm text-muted-foreground text-center">
              {t('ext.internalUser') || 'Interner Benutzer?'}{' '}
              <Link to="/register" className="text-orange-500 hover:underline font-medium">{t('ext.registerInternal') || 'Hier registrieren'}</Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default ExternalRegisterPage;
